import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router";

import {
  type BadgeProps,
  Button,
  Modal,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { useMutationRequest } from "~/v2/api/hooks";
import {
  useGetFPSLStatus,
  useGetFPSLTierRates,
} from "~/v2/api/hooks/useGetFPSL";
import { queryKeys } from "~/v2/api/queryKeys";
import {
  type Account,
  type Product,
  unenrollInFPSL,
} from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { FpslBenefits } from "../../fpslOnboarding/FpslBenefits";
import { FpslEliteDisclaimer } from "../../fpslOnboarding/FpslEliteDisclaimer";
import { FeatureCard } from "./FeatureCard";

export const FPSLCard = ({
  account,
  product,
}: {
  account: Account | undefined;
  product: Product | undefined;
}) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient();

  const { status: fpslStatus } = useGetFPSLStatus({
    accountID: account?.id,
    product,
  });

  const { eliteRate, defaultRate } = useGetFPSLTierRates({
    accountID: account?.id,
    product,
  });

  const unenrollMutation = useMutationRequest(
    async () => {
      if (!account) {
        throw new Error("account required");
      }
      return unenrollInFPSL(account.id);
    },
    {
      onSuccess: () => {
        toastSuccess("Stock lending disabled successfully!");
        queryClient.invalidateQueries({ queryKey: queryKeys.fpsl.all });
        setIsOpen(false);
      },
      onError: () => {
        toastError("Failed to disable stock lending");
      },
    },
  );

  const badge: { variant: BadgeProps["variant"]; text: string } | undefined =
    fpslStatus?.status === "ACTIVE"
      ? {
          variant: "blue",
          text: `Active`,
        }
      : {
          variant: "gray",
          text: `Disabled`,
        };

  return (
    <>
      <FeatureCard
        title="Stock Lending"
        badge={badge}
        body={
          fpslStatus?.status === "ACTIVE" ? (
            // TODO (FPSL): Add homescreen interest display when available
            // <div>
            //   You're earning extra income on your fully-paid stocks. You'll see
            //   the interest paid on your homescreen and monthly statement.{" "}
            // </div>
            <div>
              You're earning extra income on your fully-paid stocks. You'll see
              the interest paid on your monthly statement.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <span>
                Earn extra income<sup>*</sup> by lending your stocks. We handle
                the lending, you keep full economic ownership and get paid.
              </span>
              <FpslEliteDisclaimer
                eliteRate={eliteRate}
                defaultRate={defaultRate}
              />
            </div>
          )
        }
        button={{
          text:
            fpslStatus?.status === "ACTIVE" ? "Disable" : "Start Application",
          onClick: () => {
            if (fpslStatus?.status === "ACTIVE") {
              setIsOpen(true);
            } else {
              history.push(Path.ROUTE_FPSL_ONBOARDING);
            }
          },
        }}
      />
      <Modal
        title="Are you sure you want to disable stock lending?"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        size="lg"
        controls={
          <div className="flex flex-row gap-4">
            <Button
              variant="v2/stroke"
              onClick={() => unenrollMutation.mutate(undefined)}
              loading={unenrollMutation.isPending}
            >
              Disable
            </Button>
            <Button variant="v2/filled" onClick={() => setIsOpen(false)}>
              Keep it Active
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <FpslBenefits eliteRate={eliteRate} />
          <span className="text-gray-700 text-sm">
            If you currently have shares on loan, they'll be returned to your
            account by the next trading day. You'll stop earning interest
            immediately, with a final payout on the last settlement date of next
            month. To start earning again, you'll need to re-apply.
          </span>
        </div>
      </Modal>
    </>
  );
};
